import { Ref, useCallback, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { DocViewerFact } from './doc-fact';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { DocFact } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { AddFactDialog } from '@/new/pages/matters/:matterId/subsets/:subsetId/facts/components/dialogs/add-fact-dialog';

interface DocViewerFactSidebarProps {
  docId: string;
  docName?: string;
  factTotal: number | null;
  isLoading: boolean;
  jumpToHighlightArea: (id: string) => void;
  highlightedFactId: string | null;
  selectFact: (fact: DocFact) => void;
  facts: DocFact[];
  listRef: Ref<List>;
}

export const DocViewerFactSidebar = ({
  docId,
  docName,
  factTotal,
  jumpToHighlightArea,
  highlightedFactId,
  selectFact,
  facts,
  listRef,
  isLoading,
}: DocViewerFactSidebarProps) => {
  const { subsetId, matterId } = useParams();
  if (!matterId || !subsetId || !docId) return null;

  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [factAdderModalIsOpen, setFactAdderModalIsOpen] = useState(false);

  const handleAskKim = () => {
    trackCustomEvent(TrackingEventsV2.ThreadAskFromDocViewer, { docId });
    // Pre-fill the new Kim input with a mention of this document so the user can ask about it directly.
    const sanitizedName = (docName ?? '').replace(/[[\]()]/g, '');
    const searchParams = new URLSearchParams();
    searchParams.set('question', `@[${sanitizedName}](${docId}) `);
    navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim?${searchParams.toString()}`);
  };

  const handleNewFactCreated = useCallback(() => {
    setFactAdderModalIsOpen(false);
    trackCustomEvent(TrackingEventsV2.FactCreateFromDocViewer, { matterId, docId });
  }, [matterId, docId]);

  const Row = ({ index, style }: ListChildComponentProps) => {
    const fact = facts[index];
    if (!fact) return null;

    return (
      <div style={style} className="flex items-center gap-2 py-2 pl-0.5">
        <Button size="icon" onClick={() => jumpToHighlightArea(fact.id)} className="rounded-full border bg-gray-100">
          <Icon className="lucide--search h-3 w-3" />
        </Button>
        <div
          id={`fact-${fact.id}`}
          className={`flex-grow cursor-pointer rounded-md border bg-white shadow-sm transition-all duration-300 ease-in-out hover:bg-gray-100 ${
            highlightedFactId === fact.id ? 'border-teal-500' : ''
          }`}
          onClick={() => {
            trackCustomEvent(TrackingEventsV2.DocViewerJumpToFact, { docId });
            selectFact(fact);
            jumpToHighlightArea(fact.id);
          }}
        >
          {<DocViewerFact fact={fact} />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-5/12 flex-col border-l bg-gray-50">
      <div className="flex h-14 w-full items-center justify-between border-b bg-white pl-4 font-medium">
        <div className="flex items-center gap-2">
          <Icon className="lucide--list-check" />
          Facts <span className="text-sm text-gray-500">{factTotal ? `(${factTotal})` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleAskKim} className="text-xs">
            <Icon className="lucide--bot" />
            Ask Kim
          </Button>

          <PermissionGate permission={Permission.FactCreate} uiMode="disabled">
            <Button onClick={() => setFactAdderModalIsOpen(true)} className="text-xs">
              <Icon className="lucide--plus" />
              Add Fact
            </Button>
          </PermissionGate>
        </div>
      </div>
      <div id="document-facts-container" className="flex-1 overflow-y-auto" ref={containerRef}>
        <div className="flex h-full flex-col gap-2 bg-gray-50 p-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Icon className="lucide--loader-circle animate-spin duration-700" />
            </div>
          ) : facts.length > 0 ? (
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => (
                // @ts-ignore: React window
                <List ref={listRef} height={height} itemCount={facts.length} itemSize={72} width={width}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <Icon className="lucide--circle-question-mark" />
              <span className="text-sm text-gray-500">Could not find any facts in this document.</span>
            </div>
          )}
        </div>
      </div>
      <AddFactDialog
        isOpen={factAdderModalIsOpen}
        onOpenChange={setFactAdderModalIsOpen}
        docId={docId}
        handleNewFactCreated={handleNewFactCreated}
      />
    </div>
  );
};
